import { calculateProLineTotals, resolveTierLinePricing, round3 } from './pricing';
import { calculateLineCommission } from './cart';
import type { StatusBadgeVariant } from './status';
import type { ProArticle, ProArticlePricing, RemiseTier } from './types';

/**
 * Demandes de produit — un électricien peut ajouter au panier un produit que
 * le catalogue ne contient pas : juste un libellé et une quantité. La demande
 * voyage avec le devis, et le commerçant la traite depuis son tableau de bord.
 *
 *   pending      → personne ne l'a encore regardée
 *   fulfilled    → remplacée par un vrai article, désormais ligne du devis
 *   unavailable  → introuvable / non fournissable, aucune ligne créée
 */
export type ProductRequestStatus = 'pending' | 'fulfilled' | 'unavailable';

export const PRODUCT_REQUEST_STATUSES: readonly ProductRequestStatus[] = [
  'pending',
  'fulfilled',
  'unavailable',
] as const;

export function productRequestBadgeVariant(
  status: ProductRequestStatus,
): StatusBadgeVariant {
  if (status === 'fulfilled') return 'success';
  if (status === 'unavailable') return 'danger';
  return 'warning';
}

/** Ce que le domaine a besoin de savoir d'une demande — pas la ligne DB. */
export interface ProductRequestLike {
  status: ProductRequestStatus;
  notifiedAt?: string | null;
  resolvedArticleId?: string | null;
}

export interface ProductRequestSummary {
  total: number;
  pending: number;
  fulfilled: number;
  unavailable: number;
  /** Traitées = tout sauf `pending`. */
  resolved: number;
  /** Il reste au moins une demande à traiter. */
  hasPending: boolean;
  /**
   * Le lot peut être envoyé : au moins une demande, plus aucune en attente,
   * et au moins une décision jamais annoncée à l'électricien.
   *
   * `notifiedAt` est remis à NULL par la RPC dès qu'une demande est re-décidée,
   * donc un lot corrigé après coup redevient envoyable — sans quoi le
   * commerçant n'aurait aucun moyen d'annoncer sa correction.
   */
  canNotify: boolean;
  /** Toutes les décisions ont déjà été annoncées. */
  notified: boolean;
}

export function summarizeProductRequests(
  requests: ReadonlyArray<ProductRequestLike>,
): ProductRequestSummary {
  let pending = 0;
  let fulfilled = 0;
  let unavailable = 0;
  let unannounced = 0;

  for (const r of requests) {
    if (r.status === 'pending') pending += 1;
    else if (r.status === 'fulfilled') fulfilled += 1;
    else unavailable += 1;
    if (r.status !== 'pending' && !r.notifiedAt) unannounced += 1;
  }

  const total = requests.length;
  const resolved = fulfilled + unavailable;
  return {
    total,
    pending,
    fulfilled,
    unavailable,
    resolved,
    hasPending: pending > 0,
    canNotify: total > 0 && pending === 0 && unannounced > 0,
    notified: total > 0 && pending === 0 && unannounced === 0,
  };
}

/** Forme minimale d'une ligne de devis manipulée ici (pas de type DB). */
export interface ProDevisLineLike {
  articleId: string;
  quantite: number;
  pht: number;
  pttc: number;
  tva: number;
  remise: number;
  montantRemise: number;
}

/**
 * Retrouve le prix unitaire HT figé sur une ligne existante.
 *
 * `pht = qté × unitHt × (1 − remise/100)` — on inverse. Nécessaire parce que
 * `pro_devis_lines` ne stocke pas le prix unitaire : fusionner une substitution
 * dans une ligne déjà présente demande de reconstruire la ligne entière plutôt
 * que d'ajouter des montants au petit bonheur.
 */
export function deriveLineUnitHt(line: ProDevisLineLike): number {
  const factor = 1 - (line.remise || 0) / 100;
  const denominator = line.quantite * factor;
  if (!(denominator > 0)) return 0;
  return round3(line.pht / denominator);
}

export interface FulfilmentInput {
  article: ProArticle;
  pricing: ProArticlePricing | null;
  /** Palier du devis — la substitution se tarife comme le reste du devis. */
  tier: RemiseTier;
  quantity: number;
}

export interface FulfilmentLine extends ProDevisLineLike {
  unitHt: number;
  /** Coût net HT, pour la commission. */
  purchasePrice: number;
}

/** Construit la ligne de devis qui remplace une demande. */
export function buildFulfilmentLine(input: FulfilmentInput): FulfilmentLine {
  const { unitHt, remisePct } = resolveTierLinePricing(
    input.article,
    input.pricing,
    input.tier,
  );
  const totals = calculateProLineTotals({
    quantity: input.quantity,
    unitHt,
    remisePct,
    vatRate: input.article.vatRate,
  });
  return {
    articleId: input.article.id,
    quantite: input.quantity,
    pht: totals.pht,
    pttc: totals.pttc,
    tva: input.article.vatRate,
    remise: remisePct,
    montantRemise: totals.montantRemise,
    unitHt,
    purchasePrice: input.article.purchasePrice,
  };
}

/**
 * Insère la ligne de substitution dans le jeu de lignes du devis.
 *
 * Si l'article est DÉJÀ sur le devis, on fusionne (quantités additionnées, la
 * ligne est recalculée depuis son prix unitaire d'origine) au lieu d'empiler un
 * doublon : deux lignes du même article rendraient ambigu le marqueur
 * « produit remplacé », qui se lit par article.
 */
export function applyFulfilmentToLines(
  lines: ReadonlyArray<ProDevisLineLike>,
  fulfilment: FulfilmentLine,
): ProDevisLineLike[] {
  const index = lines.findIndex((l) => l.articleId === fulfilment.articleId);
  if (index === -1) {
    return [...lines, stripLine(fulfilment)];
  }
  const existing = lines[index]!;
  const unitHt = deriveLineUnitHt(existing) || fulfilment.unitHt;
  const quantity = existing.quantite + fulfilment.quantite;
  const totals = calculateProLineTotals({
    quantity,
    unitHt,
    remisePct: existing.remise,
    vatRate: existing.tva,
  });
  const merged: ProDevisLineLike = {
    articleId: existing.articleId,
    quantite: quantity,
    pht: totals.pht,
    pttc: totals.pttc,
    tva: existing.tva,
    remise: existing.remise,
    montantRemise: totals.montantRemise,
  };
  return lines.map((l, i) => (i === index ? merged : stripLine(l)));
}

/**
 * Défait une substitution : retire du devis la quantité qu'elle y avait ajoutée.
 *
 * Sans ça, revenir sur un « remplacé » (le repasser en attente ou le déclarer
 * indisponible) laisserait sur le devis la ligne créée par la substitution —
 * l'électricien se verrait facturer un produit qu'on vient de lui refuser.
 *
 * La ligne disparaît si la quantité tombe à zéro ; elle est recalculée depuis
 * son prix unitaire d'origine sinon, exactement comme à la fusion.
 */
export function revertFulfilmentFromLines(
  lines: ReadonlyArray<ProDevisLineLike>,
  articleId: string,
  quantity: number,
): ProDevisLineLike[] {
  const index = lines.findIndex((l) => l.articleId === articleId);
  if (index === -1) return lines.map(stripLine);

  const existing = lines[index]!;
  const remaining = round3(existing.quantite - quantity);
  if (remaining <= 0) {
    return lines.filter((_, i) => i !== index).map(stripLine);
  }

  const unitHt = deriveLineUnitHt(existing);
  const totals = calculateProLineTotals({
    quantity: remaining,
    unitHt,
    remisePct: existing.remise,
    vatRate: existing.tva,
  });
  const reduced: ProDevisLineLike = {
    articleId: existing.articleId,
    quantite: remaining,
    pht: totals.pht,
    pttc: totals.pttc,
    tva: existing.tva,
    remise: existing.remise,
    montantRemise: totals.montantRemise,
  };
  return lines.map((l, i) => (i === index ? reduced : stripLine(l)));
}

function stripLine(line: ProDevisLineLike): ProDevisLineLike {
  return {
    articleId: line.articleId,
    quantite: line.quantite,
    pht: line.pht,
    pttc: line.pttc,
    tva: line.tva,
    remise: line.remise,
    montantRemise: line.montantRemise,
  };
}

export interface DevisTotals {
  pht: number;
  pttc: number;
  tva: number;
}

/** Somme d'un jeu de lignes de devis (millime par millime, comme le panier). */
export function calculateDevisTotals(
  lines: ReadonlyArray<ProDevisLineLike>,
): DevisTotals {
  let pht = 0;
  let pttc = 0;
  for (const line of lines) {
    pht = round3(pht + line.pht);
    pttc = round3(pttc + line.pttc);
  }
  return { pht, pttc, tva: round3(pttc - pht) };
}

/**
 * Commission à AJOUTER à l'en-tête du devis pour une substitution.
 *
 * On travaille en delta plutôt qu'en recalcul global : `pro_devis_lines` ne
 * stocke pas de coût unitaire (contrairement à `pro_commande_lines`), donc le
 * bénéfice des lignes déjà présentes n'est pas reconstituable. Ajouter la seule
 * commission qu'on sait calculer est exact pour la nouvelle ligne et laisse le
 * reste de l'en-tête intact.
 */
export function fulfilmentCommissionDelta(
  fulfilment: FulfilmentLine,
  commissionRatePct: number,
): number {
  return calculateLineCommission(
    {
      quantity: fulfilment.quantite,
      unitHt: fulfilment.unitHt,
      remisePct: fulfilment.remise,
      purchasePrice: fulfilment.purchasePrice,
    },
    commissionRatePct,
  );
}

/**
 * Un article donné est-il arrivé sur ce devis par une demande ?
 *
 * Le lien vit sur la demande (`resolvedArticleId`) et non sur la ligne : le
 * devis peut être réécrit entièrement par l'électricien qui modifie ses
 * quantités (`pro_update_devis_atomic` fait DELETE puis INSERT), ce qui
 * effacerait une colonne posée sur la ligne.
 */
export function buildSubstitutedArticleIndex(
  requests: ReadonlyArray<ProductRequestLike & { label?: string }>,
): Set<string> {
  const ids = new Set<string>();
  for (const r of requests) {
    if (r.status === 'fulfilled' && r.resolvedArticleId) ids.add(r.resolvedArticleId);
  }
  return ids;
}
